import { isInDateRange } from "@/utils/dateTools";
import { defineStore } from "pinia";
import { DropPool } from "@/types/dropPool";
import { VItem } from "@/types/vItem";

export type LocationRequirement = "gps-required" | "not-required";
export type ExperienceKind =
	| "gps-map"
	| "gps-ar"
	| "proximity-ar"
	| "vault"
	| "registration";
export type SupportedDropType = "gps" | "pin";

function isPoolActive(pool: DropPool): boolean {
	if (!pool.enabled) {
		return false;
	}

	return isInDateRange(
		new Date().toISOString(),
		`${pool.startDate}T${pool.dropIntervalStart}:00.000Z`,
		`${pool.endDate}T${pool.dropIntervalEnd}:00.000Z`
	);
}

function normalizeDropType(dropType?: string | null): SupportedDropType | null {
	if (dropType === "gps" || dropType === "pin") {
		return dropType;
	}
	return null;
}

interface ExperienceResolutionInput {
	routeName?: string | null;
	pools: DropPool[];
	pendingItems: VItem[];
}

interface IExperienceStoreState {
	locationRequirement: LocationRequirement;
	experienceKind: ExperienceKind;
	preferredPoolID: string | null;
	preferredDropType: SupportedDropType | null;
	resolvedRouteName: string | null;
}

export const experienceStore = defineStore("experienceStore", {
	persist: false,
	state: () =>
		({
			locationRequirement: "not-required",
			experienceKind: "registration",
			preferredPoolID: null,
			preferredDropType: null,
			resolvedRouteName: null,
		} as IExperienceStoreState),
	getters: {
		requiresGpsLocation(state: IExperienceStoreState): boolean {
			return state.locationRequirement === "gps-required";
		},
	},
	actions: {
		reset() {
			this.locationRequirement = "not-required";
			this.experienceKind = "registration";
			this.preferredPoolID = null;
			this.preferredDropType = null;
			this.resolvedRouteName = null;
		},
		resolveFromState(input: ExperienceResolutionInput) {
			const routeName = input.routeName ?? null;
			const activePools = input.pools.filter((pool) => isPoolActive(pool));
			const pendingItem =
				input.pendingItems.find((item) => item.isDeepLink) ??
				input.pendingItems[0] ??
				null;
			const pendingPool = pendingItem?.dropPoolId
				? activePools.find((pool) => pool.id === pendingItem.dropPoolId) ??
					input.pools.find((pool) => pool.id === pendingItem.dropPoolId) ??
					null
				: null;
			const gpsPool =
				activePools.find((pool) => pool.dropType === "gps") ?? null;
			const pinPool =
				activePools.find((pool) => pool.dropType === "pin") ?? null;

			let preferredPool = pendingPool;
			let preferredDropType = normalizeDropType(pendingPool?.dropType);
			let experienceKind: ExperienceKind = "registration";

			switch (routeName) {
				case "Map":
					if (!preferredPool && !preferredDropType) {
						preferredPool = gpsPool ?? pinPool;
						preferredDropType = normalizeDropType(preferredPool?.dropType);
					}
					experienceKind =
						preferredDropType === "gps" ? "gps-map" : "proximity-ar";
					break;
				case "AR":
				case "Game":
					if (!preferredPool && !preferredDropType) {
						preferredPool = pinPool ?? gpsPool;
						preferredDropType = normalizeDropType(preferredPool?.dropType);
					}
					experienceKind =
						preferredDropType === "gps" ? "gps-ar" : "proximity-ar";
					break;
				case "Wallet":
				case "Prize":
					experienceKind = "vault";
					preferredPool = null;
					preferredDropType = null;
					break;
				default:
					experienceKind = "registration";
					if (pendingPool) {
						experienceKind =
							preferredDropType === "gps" ? "gps-ar" : "proximity-ar";
					}
					break;
			}

			this.resolvedRouteName = routeName;
			this.experienceKind = experienceKind;
			this.preferredPoolID = preferredPool?.id ?? null;
			this.preferredDropType = preferredDropType;
			this.locationRequirement =
				preferredDropType === "gps" ? "gps-required" : "not-required";
		},
	},
});
